/**
 * Call History (SCRN-13, CCF-104/CCF-112) — group-by-correlation-id.
 *
 * The BFF returns the per-interaction journey RECORDS (`/calls/history`) and the
 * named EVENT entries (`/calls/events`) for the customer in context. Both carry
 * the correlation id that identifies the whole session (#213):
 *   - an ONLINE journey → the Genesys `conversationId`;
 *   - an OFFLINE journey → the Web-App-generated v4 GUID (minted once per session
 *     and reused across every ID&V step).
 *
 * The top "Call history" table must show ONE ROW PER CALL — grouped by that
 * correlation id, NOT one row per underlying API record/event (the #203/#213
 * requirement). A single session can produce several journey records (e.g. an
 * upsert whose `startedAt` shifted, or a re-verification) that all share one
 * correlation id; they collapse into a single grouped call here. Each grouped
 * call then expands to its named EVENT detail entries (CCF-112).
 *
 * This module is a PURE display transform over the two API payloads — no I/O, no
 * React — so the grouping + event-mapping is unit-testable in isolation.
 */

/** The rendered shape of a `/calls/history` journey record (a subset of the
 *  OpenAPI `CallRecord`; Genesys-native extras render as "—" when absent). */
export interface CallRecord {
  interactionId: string;
  cif: string;
  customerName?: string;
  tellerId?: string;
  agentUpn?: string;
  queue?: string;
  verificationLevel: string;
  startedAt: string;
  endedAt?: string;
  outcome?: string;
  /** Online / Offline (CCF-104). */
  callType?: string;
  /** Inbound / Outbound for an Online call (CCF-104). */
  direction?: string;
  /** The correlation id grouping the session into one call (CCF-104). */
  correlationId?: string;
  /** Wrap code — the reason for the call, Online only (CCF-104). */
  wrapCode?: string;
  /** The AI call summary from Genesys, Online only (CCF-104). */
  callSummary?: string;
}

/** The rendered shape of a `/calls/events` named "Event Details" entry
 *  (CCF-112). */
export interface CallEvent {
  interactionId: string;
  cif: string;
  agentUpn?: string;
  occurredAt: string;
  eventName: string;
  outcome: string;
  outcomeWording?: string;
  /** The correlation id of the owning call (CCF-112, #213). */
  correlationId?: string;
}

/**
 * One row in the SCRN-13 top table — a whole call (session), grouped by its
 * correlation id. The Genesys-native columns are the best value seen across the
 * member records; the times span every member.
 */
export interface GroupedCall {
  /** The correlation id that identifies this call — the row's stable key and the
   *  "GUID ID" shown in the table (the Genesys conversationId for Online, the
   *  Web-App GUID for Offline). */
  correlationId: string;
  /** Every underlying journey-record interaction id that rolled into this call.
   *  Used to attach events recorded against a bare interaction id (no
   *  correlation id) to their owning call. */
  interactionIds: string[];
  cif: string;
  customerName?: string;
  tellerId?: string;
  agentUpn?: string;
  queue?: string;
  verificationLevel: string;
  /** The earliest member `startedAt` — when the call began. */
  startedAt: string;
  /** The latest member `endedAt`, when any member has one. */
  endedAt?: string;
  outcome?: string;
  callType?: string;
  direction?: string;
  wrapCode?: string;
  callSummary?: string;
}

/** The correlation id a record/event groups under: its own correlation id, or
 *  its interaction id as a fallback for records that predate the correlation id
 *  (#165) or an event recorded before the correlation-id stamp (#213). */
export function groupKeyOf(r: {
  correlationId?: string;
  interactionId: string;
}): string {
  return r.correlationId ?? r.interactionId;
}

/**
 * Group the journey records into one {@link GroupedCall} per correlation id.
 * The BFF already returns the records newest-first, so the groups are emitted in
 * first-seen (i.e. newest-first) order — this preserves the server ordering
 * rather than re-deriving it from the timestamps. Records are folded in the
 * order supplied; a later record only OVERWRITES a Genesys-native field when it
 * actually carries a value, so a bare re-verification record never wipes an
 * enriched one. Identity fields (agent, verification level, times) take the
 * strongest/earliest/latest across the group.
 */
export function groupCallsByCorrelation(records: CallRecord[]): GroupedCall[] {
  const byKey = new Map<string, GroupedCall>();

  for (const r of records) {
    const key = groupKeyOf(r);
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, {
        correlationId: key,
        interactionIds: [r.interactionId],
        cif: r.cif,
        ...(r.customerName ? { customerName: r.customerName } : {}),
        ...(r.tellerId ? { tellerId: r.tellerId } : {}),
        ...(r.agentUpn ? { agentUpn: r.agentUpn } : {}),
        ...(r.queue ? { queue: r.queue } : {}),
        verificationLevel: r.verificationLevel,
        startedAt: r.startedAt,
        ...(r.endedAt ? { endedAt: r.endedAt } : {}),
        ...(r.outcome ? { outcome: r.outcome } : {}),
        ...(r.callType ? { callType: r.callType } : {}),
        ...(r.direction ? { direction: r.direction } : {}),
        ...(r.wrapCode ? { wrapCode: r.wrapCode } : {}),
        ...(r.callSummary ? { callSummary: r.callSummary } : {}),
      });
      continue;
    }
    // Fold another record of the same call into the existing group.
    if (!existing.interactionIds.includes(r.interactionId)) {
      existing.interactionIds.push(r.interactionId);
    }
    // Earliest start / latest end span the whole call.
    if (r.startedAt && r.startedAt < existing.startedAt) {
      existing.startedAt = r.startedAt;
    }
    if (r.endedAt && (!existing.endedAt || r.endedAt > existing.endedAt)) {
      existing.endedAt = r.endedAt;
    }
    // Non-empty values win (a bare record never clears an enriched field).
    if (r.customerName) existing.customerName = r.customerName;
    if (r.tellerId) existing.tellerId = r.tellerId;
    if (r.agentUpn) existing.agentUpn = r.agentUpn;
    if (r.queue) existing.queue = r.queue;
    if (r.outcome) existing.outcome = r.outcome;
    // callType (CCF-104 / #331 defect 1): the GROUPED row's call type must be
    // deterministic + coherent, NOT "whichever member record happened to be
    // folded last". A single call's state can flip mid-session (e.g. the live
    // Genesys interaction ends and a later same-session action records Offline),
    // which previously let a stray Offline member overwrite the Online one and
    // made the row look broken. The correlation id is the anchor: if ANY member
    // is Online, the call IS an Online call (its correlation id is the Genesys
    // conversationId), so Online is sticky and wins over a later Offline.
    if (r.callType === 'Online') {
      existing.callType = 'Online';
    } else if (r.callType && !existing.callType) {
      existing.callType = r.callType;
    }
    if (r.direction) existing.direction = r.direction;
    if (r.wrapCode) existing.wrapCode = r.wrapCode;
    if (r.callSummary) existing.callSummary = r.callSummary;
    // Highest verification level reached wins (medium > pan > none).
    if (
      verificationRank(r.verificationLevel) >
      verificationRank(existing.verificationLevel)
    ) {
      existing.verificationLevel = r.verificationLevel;
    }
  }

  // Emit in first-seen order — the BFF returns records newest-first, so the
  // grouped calls stay newest-first without re-sorting on the (display-only)
  // timestamps.
  return [...byKey.values()];
}

/** Orders verification levels so the strongest reached is kept for the group. */
function verificationRank(level: string): number {
  switch (level) {
    case 'medium':
      return 2;
    case 'pan':
      return 1;
    default:
      return 0;
  }
}

/**
 * The named EVENT detail entries (CCF-112) belonging to a grouped call. An event
 * belongs when its correlation id matches the call's, OR (for events recorded
 * before the correlation-id stamp) when its interaction id is one of the call's
 * member interaction ids. Returned newest-first.
 */
export function eventsForGroup(
  call: GroupedCall,
  events: CallEvent[],
): CallEvent[] {
  const members = new Set(call.interactionIds);
  return events
    .filter(
      (e) =>
        groupKeyOf(e) === call.correlationId || members.has(e.interactionId),
    )
    .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
}
